import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, Smartphone } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ESIMDevice {
  id: string;
  brand: string;
  model_name: string;
  is_supported: boolean;
  notes: string | null;
  sort_order: number;
}

const BRANDS = [
  'Apple', 'Samsung', 'Google', 'OnePlus', 'Xiaomi', 'Oppo', 'Huawei', 
  'Motorola', 'Sony', 'Honor', 'Vivo', 'Realme', 'Asus', 'Sharp', 
  'Rakuten', 'Nothing', 'Fairphone', 'Nokia', 'TCL', 'ZTE', 'Other'
];

export default function ESIMDeviceAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [devices, setDevices] = useState<ESIMDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [newDevice, setNewDevice] = useState({ brand: '', model_name: '', notes: '' });
  const [filterBrand, setFilterBrand] = useState<string>('all');

  useEffect(() => {
    checkAdminAndFetch();
  }, [user]);

  const checkAdminAndFetch = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'admin');

    if (!roles || roles.length === 0) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }

    setIsAdmin(true);
    fetchDevices();
  };

  const fetchDevices = async () => {
    const { data, error } = await supabase
      .from('esim_devices')
      .select('*')
      .order('brand')
      .order('sort_order')
      .order('model_name');

    if (error) {
      toast.error('Failed to load devices');
      return;
    }

    setDevices(data || []);
    setLoading(false);
  };

  const addDevice = async () => {
    if (!newDevice.brand || !newDevice.model_name) {
      toast.error('Brand and model name are required');
      return;
    }

    const { error } = await supabase.from('esim_devices').insert({
      brand: newDevice.brand,
      model_name: newDevice.model_name,
      notes: newDevice.notes || null,
      is_supported: true
    });

    if (error) {
      toast.error('Failed to add device');
      return;
    }

    toast.success('Device added');
    setNewDevice({ brand: '', model_name: '', notes: '' });
    fetchDevices();
  };

  const updateDevice = async (id: string, updates: Partial<ESIMDevice>) => {
    const { error } = await supabase
      .from('esim_devices')
      .update(updates)
      .eq('id', id);

    if (error) {
      toast.error('Failed to update device');
      return;
    }

    setDevices(devices.map(d => d.id === id ? { ...d, ...updates } : d));
    toast.success('Device updated');
  };

  const deleteDevice = async (id: string) => {
    const { error } = await supabase.from('esim_devices').delete().eq('id', id);

    if (error) {
      toast.error('Failed to delete device');
      return;
    }

    setDevices(devices.filter(d => d.id !== id));
    toast.success('Device deleted');
  };

  const filteredDevices = filterBrand === 'all' 
    ? devices 
    : devices.filter(d => d.brand === filterBrand);

  const uniqueBrands = [...new Set(devices.map(d => d.brand))].sort();

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Smartphone className="h-6 w-6" />
            eSIM Device Management
          </h1>
        </div>

        {/* Add New Device */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Add New Device</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Select value={newDevice.brand} onValueChange={(v) => setNewDevice({ ...newDevice, brand: v })}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Brand" />
                </SelectTrigger>
                <SelectContent>
                  {BRANDS.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Model name (e.g. iPhone 16 Pro)"
                value={newDevice.model_name}
                onChange={(e) => setNewDevice({ ...newDevice, model_name: e.target.value })}
                className="w-64"
              />
              <Input
                placeholder="Notes (optional)"
                value={newDevice.notes}
                onChange={(e) => setNewDevice({ ...newDevice, notes: e.target.value })}
                className="w-48"
              />
              <Button onClick={addDevice}>
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Device List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">
              Devices ({filteredDevices.length})
            </CardTitle>
            <Select value={filterBrand} onValueChange={setFilterBrand}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {uniqueBrands.map(b => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-muted-foreground">Loading...</p>
            ) : (
              <div className="max-h-[600px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Brand</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Supported</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDevices.map((device) => (
                      <TableRow key={device.id}>
                        <TableCell className="font-medium">{device.brand}</TableCell>
                        <TableCell>{device.model_name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{device.notes || '-'}</TableCell>
                        <TableCell>
                          <Switch
                            checked={device.is_supported}
                            onCheckedChange={(checked) => updateDevice(device.id, { is_supported: checked })}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteDevice(device.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
